import qs from 'qs';
import React, { useCallback, useContext } from 'react';
import type { RouterLocation, SegmentedUrl } from './routeChecks/parseRoute';
import { ForumOptions, forumSelect } from './forumTypeUtils';
import { createPath, type LocationDescriptor, parsePath } from 'history';
import { parseQuery } from './routeChecks/parseRoute';
import {siteUrlSetting} from './instanceSettings'
import { getUrlClass } from '@/server/utils/getUrlClass';
import { urlIsAbsolute } from './vulcan-lib/utils';
import { LocationContext, NavigationContext, SubscribeLocationContext } from './locationContexts';

type MaybeRelativeLocationDescriptor = LocationDescriptor|1|-1;

/** Argument to the function returned by useNavigate: URL/descriptor, history direction, or callback from current location. */
export type NavigateTo = | MaybeRelativeLocationDescriptor | ((location: RouterLocation) => MaybeRelativeLocationDescriptor);

function getLocationFromWindow(): RouterLocation {
  const pathname = window.location.pathname;
  const search = window.location.search;
  const hash = window.location.hash;
  const location = { pathname, search, hash };
  return {
    location,
    pathname,
    url: pathname + search + hash,
    hash,
    params: {},
    query: parseQuery(location),
  };
}

// React Hook which returns the page location (parsed URL and route).
// Return value contains:
// {
//   currentRoute
//     The object that was passed to addRoute.
//   RouteComponent
//     The component used to render this route.
//   location
//     The react-router location. Inconsistent between client and SSR.
//   pathname
//     All of the URL after the domain. ie if the URL is
//     "http://lesswrong.com/foo?x=1&y=abc" then pathname is "/foo?x=1&y=abc". <-- this documentatino might be false! pathname does not return this, and oli says it shouldn't
//   hash
//     The within-page location part of a URL. Ie if the URL is
//     "http://lesswrong.com/foo#abc", the hash is "#abc".
//   params
//     Parsed components of the route path. Eg if the route path is
//     "/posts/:_id/:slug?" this might be {_id:"123", slug:"abc"}.
//   query
//     Parsed object for the portion of the URL after the ?, eg if the URL is
//     "http://lesswrong.com/foo?x=1&y=abc" this will be {x:"1",y:"abc"}. If
//     the URL does not contain a ?, this is the empty object.
// }
// Does not trigger rerenders on navigation events. If you want your component
// to rerender on navigations, use useSubscribedLocation instead.
export const useLocation = (): RouterLocation => {
  return useContext(LocationContext)!;
}

// React Hook which returns the page location, formatted as in useLocation, and
// triggers a rerender whenever navigation occurs.
export const useSubscribedLocation = (): RouterLocation => {
  return useContext(SubscribeLocationContext)!;
}

function getUpdatedLocationDescriptor(currentLocation: SegmentedUrl, locationDescriptor: LocationDescriptor) {
  if (typeof locationDescriptor === 'object') {
    return { ...currentLocation, ...locationDescriptor };
  }

  const parsedLocationDescriptor = parsePath(locationDescriptor);
  const { pathname, search, hash } = parsedLocationDescriptor;
  if (hash && !pathname && !search) {
    return { ...currentLocation, hash };
  } 
  return { ...currentLocation, ...parsedLocationDescriptor };
}

export type NavigateFunction = ReturnType<typeof useNavigate>
/**
 * React Hook which returns an acessor-object for page navigation. Contains one
 * field, `history`. See https://github.com/ReactTraining/history for
 * documentation on it.
 * Use of this hook will never trigger rerenders.
 */
export const useNavigate = () => {
  const { history } = useContext(NavigationContext)!;
  return useCallback((to: NavigateTo, options?: {replace?: boolean, openInNewTab?: boolean, skipRouter?: boolean, scroll?: boolean}) => {
    const locationDescriptor: MaybeRelativeLocationDescriptor = typeof to === 'function' ? to(getLocationFromWindow()) : to;

    if (locationDescriptor === -1) {
      history.back();
    } else if (locationDescriptor === 1) {
      history.forward();
    } else {
      const updatedLocationDescriptor = getUpdatedLocationDescriptor(window.location, locationDescriptor);
      const normalizedLocation = createPath(updatedLocationDescriptor);
      const normalizedOldLocation = createPath(window.location);
      const scrollOptions = options?.scroll === false ? { scroll: false } : undefined;

      if (options?.openInNewTab) {
        window.open(normalizedLocation, '_blank')?.focus();
      } else if (options?.skipRouter) {
        // The behavior of Next's router.push when handling hash changes
        // while on the same route is either broken or deranged, so we
        // need to do something rather more complicated.
        //
        // Problem!  window.history.pushState doesn't update the value
        // that comes out of `useLocation` for hash updates, so we 
        // need to manually fire a `hashchange` event and listen for it
        // in ClientAppGenerator.
        if (options?.replace) {
          window.history.replaceState(null, '', normalizedLocation);
        } else {
          window.history.pushState(null, '', normalizedLocation);
        }
      } else if (options?.replace) {
        if (normalizedLocation !== normalizedOldLocation) {
          history.replace(normalizedLocation, scrollOptions);
        }
      } else {
        if (normalizedLocation !== normalizedOldLocation) {
          history.push(normalizedLocation, scrollOptions);
        }
      }
    }
  }, [history]);
}

// HoC which adds a `location` property to an object, which contains the page
// location (parsed URL and route). See `useLocation`.
export const withLocation = (WrappedComponent: any) => {
  return (props: AnyBecauseTodo) => (
    <LocationContext.Consumer>
      {location =>
        <WrappedComponent
          {...props}
          location={location}
        />
      }
    </LocationContext.Consumer>
  );
}

// Given a URL which might or might not have query parameters, return a URL in
// which any query parameters found in queryParameterBlacklist are removed.
export const removeUrlParameters = (url: string, queryParameterBlacklist: string[]): string => {
  if (url.indexOf("?") < 0) return url;
  const [baseUrl, queryAndHash] = url.split("?");
  const [query, hash] = queryAndHash.split("#");
  
  const parsedQuery = qs.parse(query);
  let filteredQuery: AnyBecauseTodo = {};
  for (let key of Object.keys(parsedQuery)) {
    if (!queryParameterBlacklist.includes(key)) {
      filteredQuery[key] = parsedQuery[key];
    }
  }
  
  return baseUrl + (Object.keys(filteredQuery).length>0 ? '?'+qs.stringify(filteredQuery) : '') + (hash ? '#'+hash : '');
}

interface DomainList {
  onsiteDomains: string[]
  mirrorDomains: string[]
}

const LwAfDomainWhitelist: DomainList = {
  onsiteDomains: [
    "lesswrong.com",
    "lesserwrong.com",
    "lessestwrong.com",
    "baserates.org",
    "alignmentforum.org",
    "alignment-forum.com",
    "arbital.com",
    // TODO: fix this to not use `getCommandLineArguments` anymore
    // `localhost:${getCommandLineArguments().localhostUrlPort}`,
  ],
  mirrorDomains: [
    "greaterwrong.com",
  ],
}

const URLClass = getUrlClass()
const getForumDomainWhitelist = (): ForumOptions<DomainList> => ({
  LessWrong: LwAfDomainWhitelist,
  AlignmentForum: LwAfDomainWhitelist,
  EAForum: {
    onsiteDomains: [
      'forum.effectivealtruism.org',
      'forum-staging.effectivealtruism.org',
      // TODO: fix this to not use `getCommandLineArguments` anymore
      // `localhost:${getCommandLineArguments().localhostUrlPort}`,
    ],
    mirrorDomains: ['ea.greaterwrong.com'],
  },
  default: {
    onsiteDomains: [
      new URLClass(siteUrlSetting.get()).host,
      // TODO: fix this to not use `getCommandLineArguments` anymore
      // `localhost:${getCommandLineArguments().localhostUrlPort}`,
    ],
    mirrorDomains: [],
  }
})

const getDomainWhitelist = (): DomainList => forumSelect(getForumDomainWhitelist())

export const classifyHost = (host: string): "onsite"|"offsite"|"mirrorOfUs" => {
  let urlType: "onsite"|"offsite"|"mirrorOfUs" = "offsite";
  
  // Returns true if two domains are either the same, or differ only by addition or removal of a "www."
  function isSameDomainModuloWWW(a: string, b: string) {
    return a===b || "www."+a===b || a==="www."+b;
  }

  getDomainWhitelist().onsiteDomains.forEach((domain) => {
    if (isSameDomainModuloWWW(host, domain))
      urlType = "onsite";
  })
  getDomainWhitelist().mirrorDomains.forEach((domain) => {
    if (isSameDomainModuloWWW(host, domain))
      urlType = "mirrorOfUs";
  })

  return urlType;
}

export const classifyLink = (href: string): "onsite"|"offsite"|"mirrorOfUs" => {
  if (urlIsAbsolute(href)) {
    const host = new URLClass(href).host;
    return classifyHost(host);
  } else {
    return "onsite";
  }
}

// Returns whether a string could, conservatively, possibly be a database ID.
// Used for disambiguating hashes, which in some URL formats could either point
// to an anchor within a post, or a comment on that post.
// A string could possibly be an ID if is in the range of meteor's `Random.id`.
export const looksLikeDbIdString = (str: string): boolean => {
  return /^[a-zA-Z0-9]{17}$/.test(str);
}
